import { studentDoneAfterDue } from "@edu/shared";
import { DONE_BUTTON } from "../../exercises/homework-done.ts";

/**
 * Что показать у домашки без заданий на месте доли «верно» (1.5
 * `docs/homework-scratch-design.md`): у преподавателя это «фото: 2, новых 1»,
 * и «ответ текстом» (с «новый», пока не открыт), а если сдана кнопкой без
 * фото и ответа, «отмечено «Сделал(а)»». У ученика та же ячейка, но без
 * «новых»: полей `newPhotos` и `newAnswer` у него нет. «Прислал решение» и
 * «нажал кнопку» обязаны выглядеть по-разному.
 *
 * ⚠️ Счёт здесь про работу УЧЕНИКА (правило 5.10 CLAUDE.md не задето). Число
 * новых фото есть только у преподавателя: у ученика поля `newPhotos` нет вовсе
 * (`undefined`), и ветка «новых» до него не доходит по форме данных.
 */
export interface MaterialCell {
  text: string;
  /** «Сдана после срока», как пометка у попыток. */
  late: boolean;
  /** Приглушить: ученик ещё ничего не сдал. */
  quiet: boolean;
}

export function materialCell(args: {
  counters: { photos: number; studentDoneAt: number | null; answerAt?: number | null };
  newPhotos: number | undefined;
  /** Только у преподавателя, как `newPhotos`: у ученика поля нет вовсе. */
  newAnswer?: boolean | undefined;
  dueAt: number | null;
}): MaterialCell {
  const { photos, studentDoneAt } = args.counters;
  const answerAt = args.counters.answerAt ?? null;
  const late = studentDoneAfterDue({ studentDoneAt, dueAt: args.dueAt });
  const parts: string[] = [];
  if (photos > 0) {
    const fresh = args.newPhotos ?? 0;
    parts.push(fresh > 0 ? `фото: ${photos}, новых ${fresh}` : `фото: ${photos}`);
  }
  /*
    Ответ текстом (1.8 docs/homework-scratch-design.md) отдельной частью: «прислал
    фото» и «написал ответ» это разные сдачи, и преподаватель ищет глазами ту,
    которую ещё не читал
  */
  if (answerAt !== null) {
    parts.push(args.newAnswer === true ? `${ANSWER_TEXT}, новый` : ANSWER_TEXT);
  }
  if (parts.length > 0) return { text: parts.join(" · "), late, quiet: false };
  if (studentDoneAt !== null) return { text: MARKED_TEXT, late, quiet: false };
  return { text: "ничего нет", late: false, quiet: true };
}

export const ANSWER_TEXT = "ответ текстом";

export const LATE_TEXT = "сдана после срока";

/** Ученик нажал «Сделал(а)» без фото и ответа. */
export const MARKED_TEXT = `отмечено «${DONE_BUTTON}»`;
